import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import {
  Plus,
  Trash2,
  Copy,
  Building2,
  Eye,
  EyeOff,
  X,
  UserPlus,
  ChevronDown,
  ChevronRight,
  Layers,
  Users as UsersIcon,
} from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

const ROLES = ["Owner", "Admin", "Editor", "Viewer"];
const ROLE_TONE = {
  Owner:  { bg: "#FFE4E6", text: "#BE123C" },
  Admin:  { bg: "#E0F2FE", text: "#075985" },
  Editor: { bg: "#FEF3C7", text: "#92400E" },
  Viewer: { bg: "#F1F5F9", text: "#334155" },
};
const BRAND_PALETTE = [
  "#FFE4E6", "#E0F2FE", "#F3E8FF", "#FEF3C7", "#D1FAE5", "#FFEDD5",
];

function PasswordInput({ value, onSave, testid }) {
  const [show, setShow] = useState(false);
  const [draft, setDraft] = useState(value || "");
  useEffect(() => { setDraft(value || ""); }, [value]);
  const commit = () => { if (draft !== (value || "")) onSave(draft); };
  return (
    <div className="flex items-center gap-1.5">
      <input
        type={show ? "text" : "password"}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        data-testid={testid}
        className="flex-1 min-w-0 bg-transparent text-sm text-slate-700 outline-none focus:bg-white focus:px-2 focus:rounded-md focus:border focus:border-slate-200 h-8 placeholder:text-slate-400 transition-all"
        placeholder="—"
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="h-7 w-7 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition-all"
        title={show ? "Hide password" : "Show password"}
      >
        {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

const STATUS_CYCLE = ["not_started", "in_progress", "created"];
const STATUS_META = {
  not_started: { dot: "bg-rose-500",   ring: "ring-rose-200",   label: "Not started" },
  in_progress: { dot: "bg-amber-400",  ring: "ring-amber-200",  label: "In progress" },
  created:     { dot: "bg-emerald-500", ring: "ring-emerald-200", label: "Created" },
};

function StatusDot({ status, onChange, testid }) {
  const cur = STATUS_CYCLE.includes(status) ? status : "not_started";
  const meta = STATUS_META[cur];
  const next = () => {
    const i = STATUS_CYCLE.indexOf(cur);
    onChange(STATUS_CYCLE[(i + 1) % STATUS_CYCLE.length]);
  };
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); next(); }}
      className={`h-7 w-7 rounded-full flex items-center justify-center hover:ring-2 ${meta.ring} transition-all`}
      title={`${meta.label} — click to cycle (red \u2192 yellow \u2192 green)`}
      data-testid={testid}
      aria-label={`Status: ${meta.label}`}
    >
      <span className={`block h-3.5 w-3.5 rounded-full shadow-sm ${meta.dot}`} />
    </button>
  );
}


function NewCompanyDialog({ open, onOpenChange, onCreate }) {
  const [form, setForm] = useState({ name: "", owner_name: "", owner_email: "", owner_password: "" });
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (open) setForm({ name: "", owner_name: "", owner_email: "", owner_password: "" }); }, [open]);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.name.trim()) { toast.error("Company name is required"); return; }
    setSubmitting(true);
    try {
      await onCreate(form);
      onOpenChange(false);
      toast.success("Company added");
    } catch { toast.error("Failed to add company"); }
    finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="new-company-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Add company</DialogTitle>
          <DialogDescription>Create a new account in your workspace.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Company name</Label>
            <Input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Acme Inc." className="mt-1" data-testid="new-company-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Owner name</Label>
              <Input value={form.owner_name} onChange={(e) => update("owner_name", e.target.value)} placeholder="Full name" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Owner email</Label>
              <Input type="email" value={form.owner_email} onChange={(e) => update("owner_email", e.target.value)} placeholder="owner@company.com" className="mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Owner password</Label>
            <Input type="password" value={form.owner_password} onChange={(e) => update("owner_password", e.target.value)} placeholder="••••••••" className="mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="new-company-submit" className="btn-brand">
            {submitting ? "Adding…" : "Add company"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BrandUserRow({ user, brands, currentBrandId, onUpdate, onDelete, onDuplicate }) {
  const tone = ROLE_TONE[user.role] || ROLE_TONE.Viewer;
  return (
    <div
      className="grid grid-cols-[1.1fr_1.3fr_140px_180px_88px] items-center gap-2 px-4 h-12 border-b border-slate-100 hover:bg-slate-50/60 group/row"
      data-testid={`acc-user-row-${user.id}`}
    >
      <input
        defaultValue={user.name}
        onBlur={(e) => {
          if (e.target.value !== user.name && e.target.value.trim()) onUpdate(user.id, { name: e.target.value });
          else e.target.value = user.name;
        }}
        className="bg-transparent text-sm font-medium text-slate-800 outline-none focus:bg-white focus:px-2 focus:rounded focus:border focus:border-slate-200 h-8"
        data-testid={`acc-user-name-${user.id}`}
      />
      <input
        defaultValue={user.email}
        onBlur={(e) => {
          if (e.target.value !== user.email && e.target.value.trim()) onUpdate(user.id, { email: e.target.value });
          else e.target.value = user.email;
        }}
        className="bg-transparent text-sm text-slate-600 outline-none focus:bg-white focus:px-2 focus:rounded focus:border focus:border-slate-200 h-8"
        data-testid={`acc-user-email-${user.id}`}
      />
      <Select value={user.role} onValueChange={(v) => onUpdate(user.id, { role: v })}>
        <SelectTrigger
          className="h-8 border-none rounded-md font-medium text-xs w-full justify-between px-2 hover:brightness-95 focus:ring-0 focus:ring-offset-0"
          style={{ background: tone.bg, color: tone.text }}
          data-testid={`acc-user-role-${user.id}`}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={user.brand_id || currentBrandId} onValueChange={(v) => onUpdate(user.id, { brand_id: v })}>
        <SelectTrigger className="h-8 text-xs w-full px-2 bg-slate-50 border-slate-200" data-testid={`acc-user-brand-${user.id}`}>
          <SelectValue placeholder="Move to brand" />
        </SelectTrigger>
        <SelectContent>
          {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <div className="flex items-center justify-end gap-0.5 opacity-0 group-hover/row:opacity-100 transition-opacity">
        <button
          onClick={() => onDuplicate(user)}
          className="h-8 w-8 rounded-lg hover:bg-sky-50 text-slate-400 hover:text-blue-600 flex items-center justify-center transition-all"
          data-testid={`acc-user-duplicate-${user.id}`}
          title="Duplicate"
        >
          <Copy className="h-4 w-4" />
        </button>
        <button
          onClick={() => onDelete(user.id)}
          className="h-8 w-8 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center transition-all"
          data-testid={`acc-user-delete-${user.id}`}
          title="Delete"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function AddUserInlineRow({ brandId, onAdd }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("Viewer");
  const [adding, setAdding] = useState(false);

  const reset = () => { setName(""); setEmail(""); setRole("Viewer"); };

  const submit = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setAdding(true);
    try {
      await onAdd({ brand_id: brandId, name: name.trim(), email: email.trim(), role });
      reset();
      toast.success("User added");
    } catch { toast.error("Failed to add user"); }
    finally { setAdding(false); }
  };

  return (
    <div className="grid grid-cols-[1.1fr_1.3fr_140px_180px_88px] items-center gap-2 px-4 h-12 bg-slate-50/50">
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
        placeholder="Full name"
        className="h-8 bg-white"
        data-testid={`acc-user-new-name-${brandId}`}
      />
      <Input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
        placeholder="email@company.com"
        className="h-8 bg-white"
        data-testid={`acc-user-new-email-${brandId}`}
      />
      <Select value={role} onValueChange={setRole}>
        <SelectTrigger className="h-8 bg-white" data-testid={`acc-user-new-role-${brandId}`}><SelectValue /></SelectTrigger>
        <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
      </Select>
      <span className="text-[11px] text-slate-400">Press Enter to add</span>
      <Button
        onClick={submit}
        disabled={adding}
        size="sm"
        className="btn-brand h-8 ml-auto"
        data-testid={`acc-user-add-${brandId}`}
      >
        <UserPlus className="h-3.5 w-3.5 mr-1" /> Add
      </Button>
    </div>
  );
}

function BrandSection({ brand, allBrands, users, onAddUser, onUpdateUser, onDeleteUser, onDuplicateUser, onRenameBrand, onDeleteBrand }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border-t border-slate-100" data-testid={`brand-section-${brand.id}`}>
      {/* Brand sub-header (clickable to toggle) */}
      <div
        className={`h-11 px-4 flex items-center gap-3 border-b border-slate-100 group/brand cursor-pointer transition-colors ${expanded ? "bg-white" : "bg-slate-50/60 hover:bg-white"}`}
        onClick={() => setExpanded((v) => !v)}
      >
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
          className="h-6 w-6 rounded hover:bg-slate-100 text-slate-500 flex items-center justify-center"
          data-testid={`brand-toggle-${brand.id}`}
          aria-label={expanded ? "Collapse brand" : "Expand brand"}
        >
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: brand.color }} />
        <input
          defaultValue={brand.name}
          onClick={(e) => e.stopPropagation()}
          onBlur={(e) => {
            if (e.target.value !== brand.name && e.target.value.trim()) onRenameBrand(brand.id, e.target.value);
            else e.target.value = brand.name;
          }}
          className="bg-transparent text-sm font-medium text-slate-800 outline-none focus:bg-slate-50 focus:px-2 focus:rounded h-7"
          data-testid={`brand-name-${brand.id}`}
        />
        <span className="text-[11px] text-slate-500">{users.length} {users.length === 1 ? "user" : "users"}</span>
        <button
          onClick={(e) => { e.stopPropagation(); onDeleteBrand(brand); }}
          className="ml-auto opacity-0 group-hover/brand:opacity-100 h-7 w-7 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 flex items-center justify-center transition-all"
          data-testid={`brand-delete-${brand.id}`}
          title="Delete brand"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Collapsible body */}
      {expanded && (
        <>
          {/* Column headers */}
          <div className="grid grid-cols-[1.1fr_1.3fr_140px_180px_88px] text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500 px-4 h-9 items-center bg-slate-50/40 border-b border-slate-100">
            <div>Name</div>
            <div>Email</div>
            <div>Role</div>
            <div>Brand</div>
            <div className="text-center">Actions</div>
          </div>

          {/* Users */}
          {users.length === 0 ? (
            <div className="px-4 py-3 text-center text-[12px] text-slate-400 italic border-b border-slate-100">No users yet for this brand</div>
          ) : (
            users.map((u) => (
              <BrandUserRow
                key={u.id}
                user={u}
                brands={allBrands}
                currentBrandId={brand.id}
                onUpdate={onUpdateUser}
                onDelete={onDeleteUser}
                onDuplicate={onDuplicateUser}
              />
            ))
          )}

          {/* Add new user */}
          <AddUserInlineRow brandId={brand.id} onAdd={onAddUser} />
        </>
      )}
    </div>
  );
}

function CompanyCard({
  company, brands, accountUsers,
  onUpdateCompany, onDeleteCompany, onDuplicateCompany,
  onAddBrand, onRenameBrand, onDeleteBrand,
  onAddUser, onUpdateUser, onDeleteUser, onDuplicateUser,
  embedded = false,
}) {
  const [newBrand, setNewBrand] = useState("");
  const [showNewBrand, setShowNewBrand] = useState(false);

  const usersByBrand = useMemo(() => {
    const m = {};
    for (const b of brands) m[b.id] = [];
    for (const u of accountUsers) {
      if (m[u.brand_id]) m[u.brand_id].push(u);
    }
    return m;
  }, [brands, accountUsers]);

  const submitBrand = async () => {
    const trimmed = newBrand.trim();
    if (!trimmed) return;
    const color = BRAND_PALETTE[brands.length % BRAND_PALETTE.length];
    try {
      await onAddBrand({ company_id: company.id, name: trimmed, color });
      setNewBrand("");
      setShowNewBrand(false);
      toast.success("Brand added");
    } catch { toast.error("Failed to add brand"); }
  };

  return (
    <div
      className={embedded ? "" : "bg-white rounded-2xl border border-slate-200 overflow-hidden"}
      data-testid={`company-card-${company.id}`}
    >
      {/* Company header with identity (hidden inside dialog) */}
      {!embedded && (
      <div className="grid grid-cols-[260px_1fr_1fr_1fr_88px] items-center gap-4 px-5 py-4 bg-gradient-to-br from-sky-50/60 via-white to-white border-b border-slate-200 group/company company-detail-header">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 shrink-0 rounded-lg bg-sky-50 text-blue-700 flex items-center justify-center">
            <Building2 className="h-5 w-5" />
          </div>
          <div className="flex flex-col min-w-0">
            <input
              defaultValue={company.name}
              onBlur={(e) => {
                if (e.target.value !== company.name && e.target.value.trim()) onUpdateCompany(company.id, { name: e.target.value });
                else e.target.value = company.name;
              }}
              className="bg-transparent font-display text-base font-semibold text-slate-900 outline-none focus:bg-white focus:px-2 focus:rounded focus:border focus:border-slate-200 h-7"
              data-testid={`company-name-${company.id}`}
            />
            <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Company</span>
          </div>
        </div>

        <div className="flex flex-col min-w-0">
          <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 mb-0.5">Owner Name</span>
          <input
            defaultValue={company.owner_name || ""}
            onBlur={(e) => {
              if (e.target.value !== (company.owner_name || "")) onUpdateCompany(company.id, { owner_name: e.target.value });
            }}
            placeholder="—"
            className="bg-transparent text-sm text-slate-800 outline-none focus:bg-white focus:px-2 focus:rounded focus:border focus:border-slate-200 h-7 placeholder:text-slate-400"
            data-testid={`company-owner-name-${company.id}`}
          />
        </div>

        <div className="flex flex-col min-w-0">
          <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 mb-0.5">Owner Email</span>
          <input
            defaultValue={company.owner_email || ""}
            onBlur={(e) => {
              if (e.target.value !== (company.owner_email || "")) onUpdateCompany(company.id, { owner_email: e.target.value });
            }}
            placeholder="—"
            className="bg-transparent text-sm text-slate-700 outline-none focus:bg-white focus:px-2 focus:rounded focus:border focus:border-slate-200 h-7 placeholder:text-slate-400"
            data-testid={`company-owner-email-${company.id}`}
          />
        </div>

        <div className="flex flex-col min-w-0">
          <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 mb-0.5">Password</span>
          <PasswordInput
            value={company.owner_password}
            onSave={(v) => onUpdateCompany(company.id, { owner_password: v })}
            testid={`company-owner-password-${company.id}`}
          />
        </div>

        <div className="flex items-center justify-end gap-0.5 opacity-0 group-hover/company:opacity-100 transition-opacity">
          <button
            onClick={() => onDuplicateCompany(company)}
            className="h-9 w-9 rounded-lg hover:bg-sky-50 text-slate-400 hover:text-blue-600 flex items-center justify-center transition-all"
            data-testid={`company-duplicate-${company.id}`}
            title="Duplicate company"
          >
            <Copy className="h-4 w-4" />
          </button>
          <button
            onClick={() => onDeleteCompany(company)}
            className="h-9 w-9 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center transition-all"
            data-testid={`company-delete-${company.id}`}
            title="Delete company"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      )}

      {/* Brand sections */}
      {brands.length === 0 ? (
        <div className="px-5 py-6 text-center text-sm text-slate-400">No brands yet — add one below to start.</div>
      ) : (
        brands.map((b) => (
          <BrandSection
            key={b.id}
            brand={b}
            allBrands={brands}
            users={usersByBrand[b.id] || []}
            onAddUser={onAddUser}
            onUpdateUser={onUpdateUser}
            onDeleteUser={onDeleteUser}
            onDuplicateUser={onDuplicateUser}
            onRenameBrand={onRenameBrand}
            onDeleteBrand={onDeleteBrand}
          />
        ))
      )}

      {/* Add brand */}
      <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/40">
        {showNewBrand ? (
          <div className="flex items-center gap-2">
            <Input
              autoFocus
              value={newBrand}
              onChange={(e) => setNewBrand(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitBrand(); if (e.key === "Escape") { setNewBrand(""); setShowNewBrand(false); } }}
              placeholder="Brand name"
              className="h-9 max-w-xs bg-white"
              data-testid={`new-brand-name-${company.id}`}
            />
            <Button onClick={submitBrand} size="sm" className="btn-brand h-9" data-testid={`new-brand-submit-${company.id}`}>
              <Plus className="h-4 w-4 mr-1" /> Add brand
            </Button>
            <Button variant="outline" onClick={() => { setNewBrand(""); setShowNewBrand(false); }} size="sm" className="h-9">Cancel</Button>
          </div>
        ) : (
          <button
            onClick={() => setShowNewBrand(true)}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 h-8 px-3 rounded-lg hover:bg-white border border-dashed border-slate-300 hover:border-slate-400 transition-all"
            data-testid={`add-brand-${company.id}`}
          >
            <Plus className="h-3.5 w-3.5" /> Add brand to {company.name}
          </button>
        )}
      </div>
    </div>
  );
}

export default function Accounts() {
  const [companies, setCompanies] = useState([]);
  const [brands, setBrands] = useState([]);
  const [accountUsers, setAccountUsers] = useState([]);
  const [newCompanyOpen, setNewCompanyOpen] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const load = async () => {
    try {
      const [c, b, u] = await Promise.all([
        api.get("/companies"),
        api.get("/brands"),
        api.get("/account-users"),
      ]);
      setCompanies(c.data);
      setBrands(b.data);
      setAccountUsers(u.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, []);

  const brandsByCompany = useMemo(() => {
    const m = {};
    for (const c of companies) m[c.id] = [];
    for (const b of brands) {
      if (!m[b.company_id]) m[b.company_id] = [];
      m[b.company_id].push(b);
    }
    return m;
  }, [companies, brands]);

  // Company handlers
  const handleCreateCompany = async (payload) => {
    const { data } = await api.post("/companies", payload);
    setCompanies((prev) => [...prev, data]);
  };
  const handleUpdateCompany = async (id, patch) => {
    try {
      const { data } = await api.patch(`/companies/${id}`, patch);
      setCompanies((prev) => prev.map((c) => (c.id === id ? data : c)));
    } catch { toast.error("Update failed"); }
  };
  const handleDeleteCompany = async (company) => {
    if (!window.confirm(`Delete "${company.name}"? All its brands and users will be removed.`)) return;
    try {
      await api.delete(`/companies/${company.id}`);
      setCompanies((prev) => prev.filter((c) => c.id !== company.id));
      setBrands((prev) => prev.filter((b) => b.company_id !== company.id));
      setAccountUsers((prev) => prev.filter((u) => u.company_id !== company.id));
      toast.success("Company deleted");
    } catch { toast.error("Delete failed"); }
  };

  const handleDuplicateCompany = async (company) => {
    try {
      await api.post(`/companies/${company.id}/duplicate`);
      await load();
      toast.success(`Duplicated "${company.name}"`);
    } catch { toast.error("Duplicate failed"); }
  };

  // Brand handlers
  const handleAddBrand = async (payload) => {
    const { data } = await api.post("/brands", payload);
    setBrands((prev) => [...prev, data]);
  };
  const handleRenameBrand = async (id, name) => {
    try {
      const { data } = await api.patch(`/brands/${id}`, { name });
      setBrands((prev) => prev.map((b) => (b.id === id ? data : b)));
    } catch { toast.error("Rename failed"); }
  };
  const handleDeleteBrand = async (brand) => {
    if (!window.confirm(`Delete brand "${brand.name}"? All its users will be removed.`)) return;
    try {
      await api.delete(`/brands/${brand.id}`);
      setBrands((prev) => prev.filter((b) => b.id !== brand.id));
      setAccountUsers((prev) => prev.filter((u) => u.brand_id !== brand.id));
      toast.success("Brand deleted");
    } catch { toast.error("Delete failed"); }
  };

  // Account user handlers
  const handleAddUser = async (payload) => {
    const { data } = await api.post("/account-users", payload);
    setAccountUsers((prev) => [...prev, data]);
  };
  const handleUpdateUser = async (id, patch) => {
    try {
      const { data } = await api.patch(`/account-users/${id}`, patch);
      setAccountUsers((prev) => prev.map((u) => (u.id === id ? data : u)));
    } catch { toast.error("Update failed"); }
  };
  const handleDeleteUser = async (id) => {
    try {
      await api.delete(`/account-users/${id}`);
      setAccountUsers((prev) => prev.filter((u) => u.id !== id));
      toast.success("User removed");
    } catch { toast.error("Delete failed"); }
  };
  const handleDuplicateUser = async (u) => {
    try {
      const baseEmail = u.email || "";
      const [local, domain] = baseEmail.includes("@") ? baseEmail.split("@") : [baseEmail, ""];
      const newEmail = domain ? `${local}+copy-${Date.now().toString(36).slice(-4)}@${domain}` : `${baseEmail}-copy`;
      const { data } = await api.post("/account-users", {
        brand_id: u.brand_id,
        name: `${u.name} (copy)`,
        email: newEmail,
        role: u.role,
      });
      setAccountUsers((prev) => [...prev, data]);
      toast.success("User duplicated");
    } catch { toast.error("Duplicate failed"); }
  };

  return (
    <div className="space-y-6" data-testid="accounts-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900">Companies</h2>
          <p className="text-sm text-slate-500">Each company holds its brands; users are scoped per brand</p>
        </div>
        <Button onClick={() => setNewCompanyOpen(true)} data-testid="new-company-button" className="btn-brand h-10 rounded-xl">
          <Plus className="h-4 w-4 mr-1" /> Add company
        </Button>
      </div>

      {companies.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <p className="text-sm text-slate-500">No companies yet — add your first.</p>
          <Button onClick={() => setNewCompanyOpen(true)} className="btn-brand mt-4">Add company</Button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200">
          {/* Table header */}
          <div className="sticky top-0 z-30 grid grid-cols-[36px_36px_1.1fr_1fr_1.2fr_1fr_100px_100px_88px] text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500 px-5 h-11 items-center bg-slate-100/95 backdrop-blur-sm border-b border-slate-200 shadow-[0_1px_0_rgba(0,0,0,0.03)] rounded-t-2xl">
            <div></div>
            <div className="text-center">Status</div>
            <div>Company</div>
            <div>Owner Name</div>
            <div>Owner Email</div>
            <div>Password</div>
            <div className="text-center">Brands</div>
            <div className="text-center">Users</div>
            <div className="text-center">Actions</div>
          </div>

          {companies.map((c) => {
            const cBrands = brandsByCompany[c.id] || [];
            const cUsers = accountUsers.filter((u) => u.company_id === c.id);
            const isExpanded = expandedIds.has(c.id);
            return (
              <div key={c.id} className="border-b border-slate-100 last:border-b-0" data-testid={`company-row-${c.id}`}>
                <div
                  className={`grid grid-cols-[36px_36px_1.1fr_1fr_1.2fr_1fr_100px_100px_88px] items-center gap-3 px-5 py-3 transition-colors group cursor-pointer ${
                    isExpanded ? "bg-sky-50/40" : "hover:bg-slate-50/60"
                  }`}
                  onClick={() => toggleExpanded(c.id)}
                >
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleExpanded(c.id); }}
                    className="h-8 w-8 rounded-md hover:bg-white text-slate-500 hover:text-slate-900 flex items-center justify-center transition-colors"
                    data-testid={`company-expand-${c.id}`}
                    aria-label={isExpanded ? "Collapse" : "Expand"}
                  >
                    {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>

                  <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                    <StatusDot
                      status={c.status}
                      onChange={(next) => handleUpdateCompany(c.id, { status: next })}
                      testid={`company-status-${c.id}`}
                    />
                  </div>

                  <div className="flex items-center gap-3 min-w-0" onClick={(e) => e.stopPropagation()}>
                    <div className="h-9 w-9 shrink-0 rounded-lg bg-sky-50 text-blue-700 flex items-center justify-center">
                      <Building2 className="h-[18px] w-[18px]" />
                    </div>
                    <input
                      defaultValue={c.name}
                      onBlur={(e) => {
                        if (e.target.value !== c.name && e.target.value.trim()) handleUpdateCompany(c.id, { name: e.target.value });
                        else e.target.value = c.name;
                      }}
                      className="flex-1 min-w-0 bg-transparent text-sm font-semibold text-slate-900 outline-none focus:bg-white focus:px-2 focus:rounded-md focus:border focus:border-slate-200 h-8"
                      data-testid={`company-name-${c.id}`}
                    />
                  </div>

                  <input
                    defaultValue={c.owner_name || ""}
                    onClick={(e) => e.stopPropagation()}
                    onBlur={(e) => {
                      if (e.target.value !== (c.owner_name || "")) handleUpdateCompany(c.id, { owner_name: e.target.value });
                    }}
                    placeholder="—"
                    className="bg-transparent text-sm text-slate-700 outline-none focus:bg-white focus:px-2 focus:rounded-md focus:border focus:border-slate-200 h-8 placeholder:text-slate-400 transition-all"
                    data-testid={`company-owner-name-${c.id}`}
                  />

                  <input
                    defaultValue={c.owner_email || ""}
                    onClick={(e) => e.stopPropagation()}
                    onBlur={(e) => {
                      if (e.target.value !== (c.owner_email || "")) handleUpdateCompany(c.id, { owner_email: e.target.value });
                    }}
                    placeholder="—"
                    className="bg-transparent text-sm text-slate-700 outline-none focus:bg-white focus:px-2 focus:rounded-md focus:border focus:border-slate-200 h-8 placeholder:text-slate-400 transition-all"
                    data-testid={`company-owner-email-${c.id}`}
                  />

                  <div onClick={(e) => e.stopPropagation()}>
                    <PasswordInput
                      value={c.owner_password}
                      onSave={(v) => handleUpdateCompany(c.id, { owner_password: v })}
                      testid={`company-owner-password-${c.id}`}
                    />
                  </div>

                  <button
                    onClick={(e) => { e.stopPropagation(); toggleExpanded(c.id); }}
                    className="h-8 rounded-md bg-slate-50 border border-slate-200 hover:border-slate-300 hover:bg-white text-xs font-medium text-slate-700 flex items-center justify-center gap-1.5 transition-colors"
                    data-testid={`company-brands-count-${c.id}`}
                  >
                    <Layers className="h-3.5 w-3.5 text-blue-600" />
                    {cBrands.length}
                  </button>

                  <button
                    onClick={(e) => { e.stopPropagation(); toggleExpanded(c.id); }}
                    className="h-8 rounded-md bg-slate-50 border border-slate-200 hover:border-slate-300 hover:bg-white text-xs font-medium text-slate-700 flex items-center justify-center gap-1.5 transition-colors"
                    data-testid={`company-users-count-${c.id}`}
                  >
                    <UsersIcon className="h-3.5 w-3.5 text-blue-600" />
                    {cUsers.length}
                  </button>

                  <div
                    className="flex items-center justify-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={() => handleDuplicateCompany(c)}
                      className="h-8 w-8 rounded-md hover:bg-sky-50 text-slate-400 hover:text-blue-600 flex items-center justify-center transition-all"
                      data-testid={`company-duplicate-${c.id}`}
                      title="Duplicate company"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteCompany(c)}
                      className="h-8 w-8 rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center transition-all"
                      data-testid={`company-delete-${c.id}`}
                      title="Delete company"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Accordion body */}
                {isExpanded && (
                  <div className="bg-slate-50/60 border-t border-slate-100 px-5 py-4 animate-fade-up" data-testid={`company-expanded-${c.id}`}>
                    <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
                      <CompanyCard
                        company={c}
                        brands={cBrands}
                        accountUsers={cUsers}
                        onUpdateCompany={handleUpdateCompany}
                        onDeleteCompany={() => {}}
                        onDuplicateCompany={() => {}}
                        onAddBrand={handleAddBrand}
                        onRenameBrand={handleRenameBrand}
                        onDeleteBrand={handleDeleteBrand}
                        onAddUser={handleAddUser}
                        onUpdateUser={handleUpdateUser}
                        onDeleteUser={handleDeleteUser}
                        onDuplicateUser={handleDuplicateUser}
                        embedded
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <NewCompanyDialog
        open={newCompanyOpen}
        onOpenChange={setNewCompanyOpen}
        onCreate={handleCreateCompany}
      />
    </div>
  );
}
